import { useState } from "react";
import type { FormEvent } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncQuery, useDialogForm, useToast } from "@/ds";
import {
  alterarCotacaoObra,
  cancelarSolicitacaoCotacao,
  getCotacaoReferencia,
} from "@/modules/financeiro-obras";
import type { CotacaoReferencia } from "@/modules/financeiro-obras";
import { useChaveIdempotencia, useMoedasCatalogo } from "@/shared/hooks";
import {
  COTACAO_DEFINIR_LABEL,
  COTACAO_EDITAR_LABEL,
  COTACAO_ESCOPO_NOTA,
  COTACAO_PENDENTE_NOTA,
  COTACAO_PENDENTE_TITULO,
  COTACAO_TITLE,
} from "../planejamento-controle.constants";
import { FEL_ZERO } from "../planejamento-controle.cancelamento";
import { formatTaxa, formatVariacao } from "../planejamento-controle.format";
import {
  CAMPO_LABEL,
  CANCELAR_SOLICITACAO_LABEL,
  COTACAO_ALTERADA_MESSAGE,
  COTACAO_MAIOR_QUE_ZERO,
  COTACAO_OBRIGATORIA,
  MOEDA_PREFIXO,
  EM_APROVACAO_TAG,
  FAIXA_APROVACAO,
  RECALCULA_NOTA,
  REFERENCIA_ERRO,
  REFERENCIA_PERMISSAO,
  REFERENCIA_TEXTO,
  SALVAR_LABEL,
  SEM_REFERENCIA,
  SOLICITAR_ALTERACAO_LABEL,
  SOLICITACAO_CANCELADA_MESSAGE,
  SOLICITACAO_TEXTO,
} from "./cotacao-card.constants";
import type { CotacaoCardProps } from "./types-cotacao-card";

const dataDoPedido = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");

const SEM_REFERENCIA_CARREGADA: CotacaoReferencia = null;

function validar(valor: number | null): string | null {
  if (valor === null) return COTACAO_OBRIGATORIA;
  return valor > 0 ? null : COTACAO_MAIOR_QUE_ZERO;
}

const competenciaMesAno = (competencia: string) => competencia.split("-").reverse().join("/");

function variacaoEntre(base: number | null, valor: number | null): string | null {
  if (base === null || base <= 0 || valor === null || valor <= 0) return null;
  return formatVariacao(((valor - base) / base) * 100);
}

export function useCotacaoCard({
  obraId,
  cotacao,
  fel,
  pendente,
  editavel,
  podeDefinir,
  onAlterada,
}: CotacaoCardProps) {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const [editando, setEditando] = useState(false);
  const [definindo, setDefinindo] = useState(false);
  const [foco, setFoco] = useState(0);
  const [solicitando, setSolicitando] = useState(false);
  const { moedas } = useMoedasCatalogo({ enabled: solicitando });
  const [cancelando, setCancelando] = useState(false);
  const [erroCancelar, setErroCancelar] = useState<string | null>(null);
  const exigeAprovacao = fel !== FEL_ZERO;
  const solicitacao = cotacao?.solicitacao ?? null;

  const form = useDialogForm<{ cotacao: number | null }>({
    initial: { cotacao: cotacao?.valor ?? null },
    validate: ({ cotacao: valor }) => {
      const erro = validar(valor);
      return erro ? { cotacao: erro } : {};
    },
    onSubmit: async ({ cotacao: valor }) => {
      if (!cotacao || valor === null) return;
      const body = { cotacao: valor };
      await comChave(body, (idempotencyKey) =>
        alterarCotacaoObra(obraId, cotacao.moedaId, body, { idempotencyKey }),
      );
      toast.success(COTACAO_ALTERADA_MESSAGE);
      setEditando(false);
      onAlterada();
    },
  });

  const referencia = useAsyncQuery({
    fetcher: (signal) =>
      cotacao ? getCotacaoReferencia(cotacao.moedaId, { signal }) : Promise.resolve(null),
    queryKey: `cotacao-referencia:${cotacao?.moedaId ?? ""}`,
    initialData: SEM_REFERENCIA_CARREGADA,
    enabled: editando && cotacao !== null,
    forbiddenMessage: REFERENCIA_PERMISSAO,
    errorMessage: REFERENCIA_ERRO,
    resolveErrorMessage: getErrorMessage,
  });

  const moeda = cotacao?.moeda ?? pendente?.moeda ?? "";
  const valorEditado = form.values.cotacao;
  const variacao = variacaoEntre(referencia.data?.cotacao ?? null, valorEditado);
  const textoReferencia = referencia.data
    ? REFERENCIA_TEXTO(
        competenciaMesAno(referencia.data.competencia),
        formatTaxa(referencia.data.cotacao),
      )
    : SEM_REFERENCIA;

  const modo = editando ? "edicao" : cotacao ? "consulta" : "pendente";

  return {
    modo,
    titulo: pendente && !cotacao ? COTACAO_PENDENTE_TITULO(moeda) : COTACAO_TITLE,
    valor: cotacao ? `${moeda} = ${formatTaxa(cotacao.valor)}` : null,
    nota: pendente && !cotacao ? COTACAO_PENDENTE_NOTA(moeda) : COTACAO_ESCOPO_NOTA,
    editarLabel: COTACAO_EDITAR_LABEL,
    definirLabel: COTACAO_DEFINIR_LABEL,
    podeEditar: editavel && cotacao !== null && solicitacao === null,
    solicitacao:
      cotacao && solicitacao
        ? {
            tag: EM_APROVACAO_TAG,
            texto: SOLICITACAO_TEXTO(
              formatTaxa(solicitacao.cotacaoSolicitada),
              variacaoEntre(cotacao.valor, solicitacao.cotacaoSolicitada),
              dataDoPedido(solicitacao.solicitadaEm),
              formatTaxa(cotacao.valor),
            ),
            cancelarLabel: CANCELAR_SOLICITACAO_LABEL,
            podeCancelar: editavel,
            cancelando,
            erro: erroCancelar,
          }
        : null,
    salvarLabel: exigeAprovacao ? SOLICITAR_ALTERACAO_LABEL : SALVAR_LABEL,
    podeDefinir: podeDefinir && pendente?.moedaId != null,
    edicao: {
      moedaPrefixo: MOEDA_PREFIXO(moeda),
      campoLabel: CAMPO_LABEL,
      valor: valorEditado,
      erro: form.errors.cotacao ?? null,
      foco,
      referencia: [
        textoReferencia,
        ...(variacao ? [variacao] : []),
        cotacao && exigeAprovacao
          ? FAIXA_APROVACAO(fel, formatTaxa(cotacao.valor))
          : RECALCULA_NOTA,
      ].join(" · "),
      referenciaCarregando: referencia.status === "loading",
      erroSalvar: form.submitError,
      salvando: form.isSubmitting,
    },
    solicitarDialog:
      solicitando && cotacao && valorEditado !== null
        ? {
            obraId,
            moeda: {
              id: cotacao.moedaId,
              codigo: cotacao.moeda,
              nome: moedas.find((m) => m.id === cotacao.moedaId)?.nome ?? null,
            },
            vigente: cotacao.valor,
            nova: valorEditado,
            onClose: () => setSolicitando(false),
            onSolicitada: () => {
              setSolicitando(false);
              setEditando(false);
              form.reset({ cotacao: cotacao.valor });
              onAlterada();
            },
          }
        : null,
    cotacaoDialog:
      definindo && pendente?.moedaId
        ? {
            obraId,
            moeda: { id: pendente.moedaId, codigo: pendente.moeda },
            onClose: () => setDefinindo(false),
            onDefinida: () => {
              setDefinindo(false);
              onAlterada();
            },
          }
        : null,
    editar: () => {
      form.reset({ cotacao: cotacao?.valor ?? null });
      setEditando(true);
    },
    descartar: () => {
      form.reset({ cotacao: cotacao?.valor ?? null });
      setEditando(false);
    },
    digitar: (valor: number | null) => form.setValue("cotacao", valor),
    salvar: (evento: FormEvent<HTMLFormElement>) => {
      evento.preventDefault();
      const invalida = validar(form.values.cotacao) !== null;
      if (invalida) setFoco((atual) => atual + 1);
      if (exigeAprovacao && !invalida) setSolicitando(true);
      else void form.submit();
    },
    cancelarSolicitacao: async () => {
      if (!cotacao || !solicitacao || cancelando) return;
      setCancelando(true);
      setErroCancelar(null);
      try {
        await comChave({ solicitacaoId: solicitacao.id }, (idempotencyKey) =>
          cancelarSolicitacaoCotacao(obraId, cotacao.moedaId, solicitacao.id, { idempotencyKey }),
        );
        toast.success(SOLICITACAO_CANCELADA_MESSAGE);
        onAlterada();
      } catch (erro) {
        setErroCancelar(getErrorMessage(erro));
      } finally {
        setCancelando(false);
      }
    },
    definir: () => setDefinindo(true),
  };
}
